/**
 * 의심 계정 관리 설정
 * 
 * 작성일: 2025-11-15
 * 목적: 붐이벤트 당첨자 선정 시 의심 계정 가중치 적용
 * 
 * 업데이트 방법:
 * 1. 이 파일만 수정
 * 2. npm run build
 * 3. 브라우저 새로고침
 */

/**
 * Level 2: 극심한 자동화 (5% 가중치)
 * - 60초 미만 간격 50% 이상
 * - 물리적으로 불가능한 참여 패턴
 */
export const LEVEL2_EXTREME_AUTOMATION = [
  'RC2D03',  // kjh9246 - 75.0% 자동화
  '4VLJR8',  // les4243 - 52.7% 자동화
  'FLN4AQ',  // yss6012 - 32.6% 자동화
  'PO8AXP',  // les-network automation
  'PFHPFE',  // 나르샤2801 계열
  'U1S8VN',  // 에어1004 조직
  'UK1YML',  // Happy7208
];

/**
 * Level 3: 심각한 자동화 (10% 가중치)
 * - 60초 미만 간격 15% 이상
 * - 명백한 BOT 패턴
 */
export const LEVEL3_SERIOUS_AUTOMATION = [
  'QLX2EK',  // ys3364 - 40.5% 자동화
  '9AZP36',  // (분석 필요)
  '1NQLZ8',  // jtk0770 - 22.5% 자동화
  'GB6MMO',  // (분석 필요)
  'S9SOEZ',  // Kang7243 - 15.7% 자동화
  'VXJYSM',  // j6782 - 1,121명 추천
];

/**
 * Level 4: 의심 자동화 (30% 가중치)
 * - 조직적 대량 생성 패턴
 * - 닉네임 패턴 의심
 */
export const LEVEL4_SUSPICIOUS_AUTOMATION = [
  'TCECBG',  // ysh9330 - 실제+BOT 혼합
  'NIEV5Q',  // (분석 필요)
  'P39X4G',  // pjw6899 - 조직적 대량 생성
  'L8YM61',  // phs1119 - 조직적 대량 생성
  'TTVKFL',  // (분석 필요)
];

/**
 * Level 5: 기타 의심 (50% 가중치)
 * - 추가 조사 필요
 */
export const LEVEL5_OTHER_SUSPICIOUS = [
  'C46WDW',
  '6EXUGF',
  '69DK1M',
  '7IRR4K',
  'AGK0CZ',
  'G1FY10',
  '9FP7MI',
  '4LVBFD',
  'BMWEHY',
  'Z0CXEC',
  'V1HLW4',
  'A88888',
];

/**
 * 전체 의심 계정 리스트 (1~3등 완전 배제용)
 */
export const ALL_SUSPICIOUS_ACCOUNTS = [
  ...LEVEL2_EXTREME_AUTOMATION,
  ...LEVEL3_SERIOUS_AUTOMATION,
  ...LEVEL4_SUSPICIOUS_AUTOMATION,
  ...LEVEL5_OTHER_SUSPICIOUS,
];

/**
 * 가중치 계산 함수
 * @param referredBy 추천인 코드
 * @param myReferralCode 본인 추천코드 (자기추천 체크용)
 * @returns 가중치 (0.03 ~ 1.0)
 */
export function calculateSuspiciousWeight(
  referredBy: string | null | undefined,
  myReferralCode: string | null | undefined
): number {
  // Level 1: 자기 추천 (3%)
  if (referredBy && myReferralCode && referredBy === myReferralCode) {
    return 0.03;
  }

  if (!referredBy) {
    return 1.0; // 추천인 없음 = 정상
  }

  // Level 2: 극심한 자동화 (5%)
  if (LEVEL2_EXTREME_AUTOMATION.includes(referredBy)) {
    return 0.05;
  }

  // Level 3: 심각한 자동화 (10%)
  if (LEVEL3_SERIOUS_AUTOMATION.includes(referredBy)) {
    return 0.1;
  }

  // Level 4: 의심 자동화 (30%)
  if (LEVEL4_SUSPICIOUS_AUTOMATION.includes(referredBy)) {
    return 0.3;
  }

  // Level 5: 기타 의심 (50%)
  if (LEVEL5_OTHER_SUSPICIOUS.includes(referredBy)) {
    return 0.5;
  }

  // Level 6: 정상 계정 (100%)
  return 1.0;
}

/**
 * 의심 계정 여부 확인
 * @param referredBy 추천인 코드
 * @returns true: 의심 계정, false: 정상 계정
 */
export function isSuspiciousAccount(referredBy: string | null | undefined): boolean {
  if (!referredBy) return false;
  return ALL_SUSPICIOUS_ACCOUNTS.includes(referredBy);
}

